<template>
    <div dusk="PageDisplay">
        <h3 class="mb-3 text-[13px] font-medium text-theme-text">
            {{ localizations[lang].pageLayout }}
        </h3>
        <div
            v-if="changeLayoutNotAllowed"
            class="max-w-xs rounded-md border border-theme-border p-2 text-sm text-theme-text"
        >
            {{ localizations[lang].changeLayoutNotAllowed }}
        </div>
        <div v-else class="grid grid-cols-3 gap-2">
            <!-- Horizontal Single -->
            <SettingsButton dusk="goSingle" variant="card" :is-active="singlePageMode" @click="goSingle">
                <svg aria-hidden="true" width="24" height="18" viewBox="0 0 32 24" fill="none">
                    <rect
                        x="10"
                        y="4"
                        width="12"
                        height="16"
                        rx="1.5"
                        stroke="currentColor"
                        stroke-width="1.5"
                        fill="none"
                    />
                    <path
                        d="M5 12L8 9.5M5 12L8 14.5M27 12L24 9.5M27 12L24 14.5"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                <SettingsButtonLabel>{{ localizations[lang].onePage }}</SettingsButtonLabel>
            </SettingsButton>

            <!-- Horizontal Double -->
            <SettingsButton dusk="goDouble" variant="card" :is-active="doublePageMode" @click="goDouble">
                <svg aria-hidden="true" width="24" height="18" viewBox="0 0 32 24" fill="none">
                    <rect
                        x="6"
                        y="4"
                        width="9"
                        height="16"
                        rx="1.5"
                        stroke="currentColor"
                        stroke-width="1.5"
                        fill="none"
                    />
                    <rect
                        x="17"
                        y="4"
                        width="9"
                        height="16"
                        rx="1.5"
                        stroke="currentColor"
                        stroke-width="1.5"
                        fill="none"
                    />
                    <path
                        d="M2 12L4 10M2 12L4 14M30 12L28 10M30 12L28 14"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                <SettingsButtonLabel>{{ localizations[lang].twoPages }}</SettingsButtonLabel>
            </SettingsButton>

            <!-- Vertical Single (scroll) -->
            <SettingsButton
                v-if="showPreviewModeOption"
                dusk="goScroll"
                variant="card"
                :is-active="scrollPageMode"
                @click="goScroll"
            >
                <svg aria-hidden="true" width="24" height="18" viewBox="0 0 32 24" fill="none">
                    <rect
                        x="10"
                        y="4"
                        width="12"
                        height="16"
                        rx="1.5"
                        stroke="currentColor"
                        stroke-width="1.5"
                        fill="none"
                    />
                    <path
                        d="M16 1L13.5 3.5M16 1L18.5 3.5M16 23L13.5 20.5M16 23L18.5 20.5"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                <SettingsButtonLabel>{{ localizations[lang].onePageScroll }}</SettingsButtonLabel>
            </SettingsButton>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import SettingsButton from './SettingsButton.vue';
import SettingsButtonLabel from './SettingsButtonLabel.vue';

export default {
    components: {
        SettingsButton,
        SettingsButtonLabel,
    },
    computed: {
        ...mapState(['localizations', 'lang', 'reader', 'scrollMode', 'isFixedLayout', 'fixedLayoutRulesOutSpreads']),
        ...mapGetters([
            'isPreviewMode',
            'currentPage',
            'currentPageIsFirstPage',
            'currentPageIsLastPage',
            'singlePageMode',
            'doublePageMode',
            'scrollPageMode',
            'onlyDesktopVisible',
            'isPdf',
            'isEpub',
        ]),
        // A lone "One page" card would still save `single` as the layout of every other book.
        changeLayoutNotAllowed() {
            return this.isEpub && this.fixedLayoutRulesOutSpreads;
        },
        showPreviewModeOption() {
            if (this.isEpub && this.isFixedLayout) {
                return false;
            }
            return this.isPdf || (this.isEpub && !this.isPreviewMode);
        },
    },
    methods: {
        ...mapMutations(['setReaderLayout']),
        ...mapActions(['trackEvent', 'goToPrevPage', 'goToNextPage', 'saveUserSettings']),
        goDouble() {
            if (this.currentPage % 2 !== 0 && !this.currentPageIsFirstPage && this.isPdf) {
                this.goToPrevPage({ dont_track: true });
            }
            if (this.isPdf) {
                this.setReaderLayout('double');
                this.saveUserSettings({ pdfLayout: 'double' });
            }
            this.$bus.$emit('layout', 'double');
        },
        goSingle() {
            if (this.isPdf) {
                this.setReaderLayout('single');
                this.saveUserSettings({ pdfLayout: 'single' });
            }
            this.$bus.$emit('layout', 'single');
        },
        goScroll() {
            if (this.isPdf) {
                this.setReaderLayout('scroll');
                this.saveUserSettings({ pdfLayout: 'scroll' });
            }
            this.$bus.$emit('layout', 'scroll');
        },
    },
};
</script>
